/**
 * Mock Invitation Validation Service
 *
 * 🔧 DEVELOPMENT ONLY — replace with real API call in production.
 *
 * Deterministic mock responses based on invitation token.
 * Each token triggers a specific business scenario for development & QA.
 *
 * The signup-completion + SMS halves of the signup flow now live in the
 * signup adapter (api/adapters/signup.ts). This file remains responsible
 * only for validating the invitation token that loads the signup page.
 *
 * === Test Scenarios ===
 *
 * | Token                 | Scenario                     | Expected Behavior                          |
 * |-----------------------|------------------------------|--------------------------------------------|
 * | `(no token)`         | Valid invitation (default)    | Form loads with default Doctor account      |
 * | `valid-invitation`    | Valid invitation (Doctor)    | Form loads with email, dept, role prefilled |
 * | `doctor-invitation`   | Valid invitation (Doctor)    | DOCTOR role, 신경과 department              |
 * | `nurse-invitation`    | Valid invitation (Nurse)     | NURSE role, 정신건강의학과 department        |
 * | `expired-invitation`  | Expired invitation link      | Error: "초대 링크가 만료되었습니다."         |
 * | `registered-invitation`| Already registered account  | Error: "이미 가입된 계정입니다."             |
 * | `invalid-invitation`  | Invalid/malformed token      | Error: "유효하지 않은 초대 링크입니다."      |
 * | `server-error`        | Server error                 | Error: server error with retry option        |
 *
 * === Example URLs ===
 *
 * /signup?token=valid-invitation
 * /signup?token=doctor-invitation
 * /signup?token=nurse-invitation
 * /signup?token=expired-invitation
 * /signup?token=registered-invitation
 * /signup?token=invalid-invitation
 * /signup?token=server-error
 *
 * === Production Migration ===
 *
 * To swap with real API:
 * 1. Replace `fetchMockInvitation(token)` call with `GET /auth/invitations/:token`
 * 2. Map the API response to `InvitationResponse` type
 * 3. Delete this file
 */

import type { PortalRole } from "@/shared/types/auth";

/** Valid invitation response — prefills signup form fields */
export interface InvitationSuccess {
	valid: true;
	email: string;
	institution: string;
	department: string;
	role: PortalRole;
}

/** Error response — blocks signup form, shows error UI */
export interface InvitationError {
	valid: false;
	reason: "expired" | "already-registered" | "invalid" | "server-error";
}

export type InvitationResponse = InvitationSuccess | InvitationError;

/** Mock lookup table — token → response. Empty string = no token = valid default. */
const MOCK_TOKENS: Record<string, InvitationResponse | undefined> = {
	"": {
		valid: true,
		email: "doctor.default@uuh.ulsan.kr",
		institution: "울산대학교병원",
		department: "정신건강의학과",
		role: "DOCTOR",
	},
	"valid-invitation": {
		valid: true,
		email: "doctor.new@uuh.ulsan.kr",
		institution: "울산대학교병원",
		department: "정신건강의학과",
		role: "DOCTOR",
	},
	"doctor-invitation": {
		valid: true,
		email: "doctor.invite@uuh.ulsan.kr",
		institution: "울산대학교병원",
		department: "신경과",
		role: "DOCTOR",
	},
	"nurse-invitation": {
		valid: true,
		email: "nurse.invite@uuh.ulsan.kr",
		institution: "울산대학교병원",
		department: "정신건강의학과",
		role: "NURSE",
	},
	"unverified-mobile": {
		valid: true,
		email: "unverified@uuh.ulsan.kr",
		institution: "울산대학교병원",
		department: "정신건강의학과",
		role: "DOCTOR",
	},
	"no-response": {
		valid: true,
		email: "noresponse@uuh.ulsan.kr",
		institution: "울산대학교병원",
		department: "정신건강의학과",
		role: "DOCTOR",
	},
	"expired-invitation": {
		valid: false,
		reason: "expired",
	},
	"registered-invitation": {
		valid: false,
		reason: "already-registered",
	},
	"invalid-invitation": {
		valid: false,
		reason: "invalid",
	},
	"server-error": {
		valid: false,
		reason: "server-error",
	},
};

/**
 * Mock invitation validation.
 *
 * Simulates async network request with a short delay.
 * Replace this function body with a real API call in production.
 */
export async function fetchMockInvitation(
	token: string,
): Promise<InvitationResponse> {
	// Simulate network latency
	await new Promise((resolve) => setTimeout(resolve, 600));

	const result = MOCK_TOKENS[token];

	if (!result) {
		return { valid: false, reason: "invalid" };
	}

	return result;
}
